import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-[#1a3a52] to-[#0f2535] text-white" data-testid="footer">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 mb-6 group">
              {/* Shield Icon SVG - White version */}
              <svg 
                viewBox="0 0 100 120" 
                className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              >
                {/* Shield base */}
                <path 
                  d="M50 5 L90 20 L90 55 C90 85 50 115 50 115 C50 115 10 85 10 55 L10 20 Z" 
                  fill="#2a6a7e"
                  stroke="#3a8a9e"
                  strokeWidth="2"
                />
                <path 
                  d="M50 5 L50 115 C50 115 10 85 10 55 L10 20 Z" 
                  fill="#3a8a9e"
                />
                {/* Inner circuit pattern */}
                <path 
                  d="M35 35 L35 55 L50 55" 
                  fill="none" 
                  stroke="#10b981" 
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                <path 
                  d="M50 35 L50 75" 
                  fill="none" 
                  stroke="#5ec4a8" 
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                <path 
                  d="M65 45 L65 65 L50 65" 
                  fill="none" 
                  stroke="#10b981" 
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                <circle cx="35" cy="35" r="3" fill="#10b981" />
                <circle cx="65" cy="45" r="3" fill="#5ec4a8" />
                <circle cx="50" cy="75" r="3" fill="#10b981" />
              </svg>
              
              <span className="text-2xl font-bold tracking-tight">
                <span className="text-white">DMM</span>
                <span className="text-gray-300 font-normal ml-1">Security</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Consultoria especializada em segurança da informação, governança, risco e compliance, 
              com plataforma proprietária SmartAudit para gestão integrada.
            </p>
            <div className="flex gap-3">
              <a
                href="https://linkedin.com/company/dmmsecurity"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 hover:bg-[#10b981] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="LinkedIn"
                data-testid="linkedin-link"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/dmmsecurity"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 hover:bg-[#10b981] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Instagram"
                data-testid="instagram-link"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Links Rápidos</h3>
            <nav className="space-y-3">
              {[
                { path: '/', label: 'Home' },
                { path: '/sobre', label: 'Sobre Nós' },
                { path: '/servicos', label: 'Serviços' },
                { path: '/smartaudit', label: 'SmartAudit' },
                { path: '/contato', label: 'Contato' },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-gray-400 hover:text-[#10b981] transition-colors text-sm hover:translate-x-1 transform duration-300"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Serviços</h3>
            <nav className="space-y-3">
              {[
                'Consultoria em Cybersegurança',
                'Governança, Risco e Compliance',
                'ISO 27001 & Conformidade',
                'Pentest & Forense Digital',
                'Conscientização e Treinamentos',
              ].map((service) => (
                <Link
                  key={service}
                  to="/servicos"
                  className="block text-gray-400 hover:text-[#10b981] transition-colors text-sm hover:translate-x-1 transform duration-300"
                >
                  {service}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Contato</h3>
            <div className="space-y-4">
              <a
                href="mailto:contato@dmmsecurity.com.br"
                className="flex items-start gap-3 text-gray-400 hover:text-[#10b981] transition-colors group"
                data-testid="footer-email"
              >
                <div className="w-10 h-10 bg-white/5 group-hover:bg-[#10b981]/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <Mail className="w-5 h-5 text-[#10b981]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">E-mail</p>
                  <span className="text-sm">contato@dmmsecurity.com.br</span>
                </div>
              </a>

              <a
                href="https://wa.me/5511987528279"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-gray-400 hover:text-[#10b981] transition-colors group"
                data-testid="footer-phone"
              >
                <div className="w-10 h-10 bg-white/5 group-hover:bg-[#10b981]/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <Phone className="w-5 h-5 text-[#10b981]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Telefone / WhatsApp</p>
                  <span className="text-sm">(11) 4326-6707</span>
                  <br />
                  <span className="text-sm">(11) 9 8752-8279</span>
                </div>
              </a>

              <div className="flex items-start gap-3 text-gray-400">
                <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#10b981]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Localização</p>
                  <span className="text-sm">Rua Canal de Suez, 357</span>
                  <br />
                  <span className="text-sm">Barueri / SP</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {currentYear} DMM Soluções em Tecnologia e Segurança LTDA. Todos os direitos reservados.
            </p>
            <p className="text-gray-600 text-xs">
              CNPJ: 26.566.276/0001-25
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
